"use client";

import { useQuery } from "@apollo/client";
import {
  Card,
  PageEmptyStateV2,
  PageLoadingStateV2,
} from "components/ui/v2";
import { AlertTriangle, Award, Send, Star, ThumbsUp } from "lucide-react";
import { gqlRecognition } from "gql";
import type { IRecognitionsResponse } from "gql/Recognitions/queries";
import useTranslation from "next-translate/useTranslation";
import { useMemo } from "react";
import { useUserStore } from "stores/user/userStore";
import { RecognitionsListV2 } from "./recognitions/RecognitionsListV2";

export interface PublicRecognitionsTabV2Props {
  userId: string;
}

export function PublicRecognitionsTabV2({ userId }: PublicRecognitionsTabV2Props) {
  const { user } = useUserStore();
  const { t } = useTranslation("common");

  const {
    data: recognitionData,
    loading: recognitionLoading,
    error: recognitionError,
  } = useQuery<IRecognitionsResponse>(gqlRecognition.queries.GET_RECOGNITIONS, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const recognitionStats = useMemo(() => {
    if (!recognitionData?.getRecognitionCount) {
      return { totalReceived: 0, totalSent: 0, uniqueValues: 0 };
    }

    const recognitions = recognitionData.getRecognitionCount;
    const totalReceived = recognitions.reduce(
      (sum, r) => sum + (r.quantityReceived || 0),
      0
    );
    const totalSent = recognitions.reduce(
      (sum, r) => sum + (r.quantitySend || 0),
      0
    );
    const uniqueValues = recognitions.length;

    return { totalReceived, totalSent, uniqueValues };
  }, [recognitionData]);

  if (recognitionLoading) {
    return (
      <PageLoadingStateV2
        title={t("Cargando reconocimientos")}
        description={t("Espere por favor")}
      />
    );
  }

  if (recognitionError) {
    return (
      <Card size="full">
        <PageEmptyStateV2
          variant="empty"
          icon={<AlertTriangle className="h-7 w-7" />}
          title={t("Error al cargar reconocimientos")}
          description={t(
            "No se pudieron cargar los reconocimientos. Intenta de nuevo más tarde."
          )}
        />
      </Card>
    );
  }

  if (
    !recognitionData?.getRecognitionCount ||
    recognitionData.getRecognitionCount.length === 0
  ) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            {t("Reconocimientos")}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
            {t("Los valores corporativos reconocidos en este colaborador")}
          </p>
        </div>
        <Card size="full">
          <PageEmptyStateV2
            variant="empty"
            icon={<Award className="h-7 w-7" />}
            title={t("No hay reconocimientos para mostrar")}
            description={t(
              "Este colaborador aún no ha recibido reconocimientos por valores corporativos."
            )}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            {t("Reconocimientos")}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
            {t("Los valores corporativos reconocidos en este colaborador")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card size="full" padding="comfortable">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {t("Recibidos")}
                </p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {recognitionStats.totalReceived}
                </p>
              </div>
              <ThumbsUp className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </Card>

          <Card size="full" padding="comfortable">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {t("Enviados")}
                </p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {recognitionStats.totalSent}
                </p>
              </div>
              <Send className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </Card>

          <Card size="full" padding="comfortable">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                  {t("Valores únicos")}
                </p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {recognitionStats.uniqueValues}
                </p>
              </div>
              <Star className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </Card>
        </div>
      </div>

      <RecognitionsListV2
        enableSeeValues={!!user}
        recognitions={recognitionData.getRecognitionCount}
        userId={userId}
      />
    </div>
  );
}
